/**
 * ParameterAdjustmentCalc — calls formula registry to compute dose from delta.
 *
 * Supports unit selection from the parameter's units map, pre-fill from
 * Phase 4 "Dose to Fix" deep-link params (delta + unit), and result_conversions.
 */
import { useState, useMemo } from 'react';
import type { Calculator } from '@reefapp/shared';
import type { ParameterAdjustmentOption } from '@reefapp/shared';
import type { ParameterAdjustmentParam } from '@reefapp/shared';
import { getFormula, UnknownFormulaError } from '@reefapp/shared';

type PACalcShape = Calculator & { kind: 'parameter_adjustment' };

export interface ParameterAdjustmentCalcProps {
  calculator: PACalcShape;
  tankVolumeGal: number;
  /** Pre-fill delta from Phase 4 deep-link. */
  prefillDelta?: string;
  /** Pre-fill unit from Phase 4 deep-link. */
  prefillUnit?: string;
}

function fmt(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2).replace(/\.?0+$/, '') : '—';
}

function unitKeys(param: ParameterAdjustmentParam): string[] {
  return Object.keys(param.units);
}

export function ParameterAdjustmentCalc({
  calculator,
  tankVolumeGal,
  prefillDelta,
  prefillUnit,
}: ParameterAdjustmentCalcProps) {
  const initialUnit =
    prefillUnit && calculator.parameter.units[prefillUnit]
      ? prefillUnit
      : calculator.parameter.defaultUnit;

  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState(initialUnit);
  const [delta, setDelta] = useState<string>(
    prefillDelta ?? '',
  );

  const option = calculator.options[selectedOptionIdx];
  if (!option) return null;

  const unitFactor = calculator.parameter.units[selectedUnit] ?? 1;
  const deltaNum = Number(delta);
  const hasAllInputs =
    tankVolumeGal > 0 && delta !== '' && Number.isFinite(deltaNum) && deltaNum > 0;

  const result = useMemo(() => {
    if (!hasAllInputs) return null;
    try {
      const fn = getFormula(option.formulaId);
      const baseDelta = deltaNum * unitFactor;
      const dose = fn(baseDelta, tankVolumeGal);
      return Number.isFinite(dose) ? dose : null;
    } catch (e: unknown) {
      if (e instanceof UnknownFormulaError) {
        return null;
      }
      throw e;
    }
  }, [hasAllInputs, option.formulaId, deltaNum, unitFactor, tankVolumeGal]);

  const baseUnitName =
    calculator.parameter.baseUnitName ?? selectedUnit;

  return (
    <div>
      <h3 className="text-lg font-semibold text-cyan-300 mb-4">
        Parameter Adjustment Calculator
      </h3>

      {/* Option selector */}
      {calculator.options.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-400 mb-2">
            Product Variant:
          </label>
          <select
            value={selectedOptionIdx}
            onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
            className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            {calculator.options.map((o: ParameterAdjustmentOption, i: number) => (
              <option key={i} value={i}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Delta input */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="col-span-2">
          <label
            htmlFor="delta-input"
            className="block text-sm font-medium text-slate-400 mb-2"
          >
            Desired Change ({baseUnitName}):
          </label>
          <input
            id="delta-input"
            type="number"
            min="0"
            step="any"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder={`e.g. 50`}
            className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500"
          />
        </div>

        <div>
          <label
            htmlFor="unit-select"
            className="block text-sm font-medium text-slate-400 mb-2"
          >
            Units:
          </label>
          <select
            id="unit-select"
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
            className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-2 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            {unitKeys(calculator.parameter).map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Formula display */}
      <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
        <p className="text-sm text-slate-400">
          {option.name}
          {option.note && (
            <span className="block text-xs text-slate-500 mt-0.5">
              {option.note}
            </span>
          )}
        </p>

        {hasAllInputs && result !== null ? (
          <div className="mt-3">
            <p className="text-2xl font-bold text-cyan-300">
              {fmt(result)} {option.output_unit}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              To raise {calculator.parameter.name} by {fmt(deltaNum)}{' '}
              {selectedUnit} in {fmt(tankVolumeGal)} gal
            </p>
            {option.result_conversions && (
              <p className="text-xs text-slate-500 mt-1">
                ≈{' '}
                {(Object.entries(option.result_conversions) as ReadonlyArray<[string, number]>)
                  .map(
                    ([unit, factor]) =>
                      `${fmt(result / factor)} ${unit}`,
                  )
                  .join(' = ')}
              </p>
            )}
          </div>
        ) : hasAllInputs && result === null ? (
          <p className="mt-3 text-sm text-red-400">
            Error: Formula "{option.formulaId}" not found.
          </p>
        ) : (
          <div className="mt-3 space-y-1">
            {tankVolumeGal <= 0 && (
              <p className="text-sm text-amber-400">
                Enter your tank volume (left sidebar).
              </p>
            )}
            {(!delta || deltaNum <= 0) && (
              <p className="text-sm text-amber-400">
                Enter the desired parameter change above.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      {calculator.notes && (
        <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
          <p className="text-xs text-slate-400 whitespace-pre-wrap">
            {calculator.notes}
          </p>
        </div>
      )}
    </div>
  );
}